import { TConvertKeys } from '@/types/common';

/**
 * Converts a snake_case string to camelCase.
 * @param str - The input string in snake_case.
 * @returns The string converted to camelCase.
 */
export const toCamelCase = (str: string): string =>
  str.replace(/_([a-z0-9])/g, (_, letter) => letter.toUpperCase());

/**
 * Converts a camelCase string to snake_case.
 * @param str - The input string in camelCase.
 * @returns The string converted to snake_case.
 */
const toSnakeCase = (str: string): string =>
  str.replace(/([A-Z])/g, '_$1').toLowerCase();

/**
 * Recursively converts all keys in an object or array between
 * snake_case and camelCase.
 * @param obj - The object or array to convert.
 * @param direction - Conversion direction: 'camel' (default) or 'snake'.
 * @returns A new object or array with keys converted.
 */
export const convertKeys = <T, D extends 'snake' | 'camel' = 'camel'>(
  obj: T,
  direction?: D,
): TConvertKeys<T, D> => {
  const convertKey = direction === 'snake' ? toSnakeCase : toCamelCase;

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeys(item, direction)) as TConvertKeys<
      T,
      D
    >;
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        convertKey(key),
        convertKeys(value, direction),
      ]),
    ) as TConvertKeys<T, D>;
  }

  return obj as TConvertKeys<T, D>;
};

/**
 * Returns the keys of an object as a typed array.
 * This ensures that the returned array preserves the key types of the object.
 * @template T - The type of the object.
 * @param obj - The object whose keys are to be retrieved.
 * @returns An array of keys of the object.
 */
export const getTypedKeys = <T extends object>(obj: T): Array<keyof T> => {
  return Object.keys(obj) as Array<keyof T>;
};

/**
 * Returns the entries of an object as a typed array.
 * Each entry is a tuple [key, value], where the key and value are typed according to the object.
 * @template T - The type of the object.
 * @param obj - The object whose entries are to be retrieved.
 * @returns An array of key-value pairs of the object.
 */
export const getTypedEntries = <T extends object>(
  obj: T,
): Array<[keyof T, T[keyof T]]> => {
  return Object.entries(obj) as Array<[keyof T, T[keyof T]]>;
};
